import { Table as AntTable, type TableProps as AntTableProps } from "antd";
import { type SorterResult, type TablePaginationConfig } from "antd/lib/table/interface";
import { useMemo } from "react";

export interface ColumnType {
  field?: string;
  type?: string;
  title?: any;
}

export interface TableProps
  extends Omit<AntTableProps<Record<string, any>>, "pagination"> {
  dataSource: any[];
  keyField?: string;
  isLoading?: boolean;
  isFetching?: boolean;
  pagination?: false | TablePaginationConfig;
  setPagination?: any;
  setSorter?: any;
  total?: number;
  columns?: ColumnType[];
  columnCountConfig?: boolean;
  showAllColumns?: boolean;
  rowClassName?: any;
}

const Table: React.FC<TableProps> = ({
  dataSource = [],
  keyField,
  isLoading,
  isFetching,
  pagination = false,
  setSorter,
  columns: columnConfig,
  rowClassName,
  scroll: userScroll,
  ...rest
}) => {
  const columns = useMemo(() => {
    const inputColumns = (columnConfig ?? []) as Record<string, any>[];
    return inputColumns.map((column, index) => {
      const key = String(column.key ?? "").toLowerCase();
      const title = String(column.title ?? "").toLowerCase();
      const dataIndex = Array.isArray(column.dataIndex)
        ? String(column.dataIndex[0] ?? "").toLowerCase()
        : String(column.dataIndex ?? "").toLowerCase();
      const isActionColumn = key.includes("action") || title.includes("action") || dataIndex === "action";
      const isLastColumn = index === inputColumns.length - 1;

      if (!isActionColumn || !isLastColumn) return column;

      return {
        ...column,
        fixed: column.fixed ?? "right",
        width: column.width ?? 180,
      };
    });
  }, [columnConfig]);

  const mergedScroll = useMemo(() => {
    if (!userScroll) return { x: "max-content" as const };
    return { ...userScroll, x: (userScroll as Record<string, any>).x ?? "max-content" };
  }, [userScroll]);

  let items = dataSource;
  if (keyField) {
    items = dataSource.map((i) => ({ ...i, key: i[keyField] }));
  }
  return (
    <AntTable
      columns={columns}
      dataSource={items}
      loading={isLoading || isFetching}
      {...(rowClassName ? { rowClassName } : null)}
      pagination={
        pagination === false
          ? { hideOnSinglePage: true, pageSize: items.length }
          : pagination
      }
      onChange={(/*tablePagination, filter,*/ sorter) => {
        const sort = sorter as any;

        setSorter?.(
          (sort?.column
            ? { ...sorter, field: sort?.column?.sortField || sort.field }
            : {}) as SorterResult<any>
        );
      }}
      showSorterTooltip={false}
      scroll={mergedScroll}
      {...rest}
    />
  );
};

export default Table;
